import {
  CalendarClock,
  LayoutPanelTop,
  Plus,
  Search,
  SlidersHorizontal,
  UserCheck,
} from "lucide-react";
import type { TaskMeta, TaskPriority, TaskState } from "../types/tasks";
import { getTaskStateLabel, taskStateOrder } from "../types/tasks";

export type TaskFilters = {
  search: string;
  estado: TaskState | "";
  prioridad: TaskPriority | "";
  proyecto: string;
  responsable: string;
  onlyMine: boolean;
  overdueOnly: boolean;
};

type Props = {
  meta: TaskMeta;
  filters: TaskFilters;
  totalTasks: number;
  overdueCount: number;
  onFiltersChange: (patch: Partial<TaskFilters>) => void;
  onOpenCreate: () => void;
};

export function TaskToolbar({
  meta,
  filters,
  totalTasks,
  overdueCount,
  onFiltersChange,
  onOpenCreate,
}: Props) {
  return (
    <section className="dashboard-component-shell overflow-hidden rounded-[28px] border border-[#dfe8e4] bg-white shadow-[0_18px_50px_rgba(15,23,42,.065)]">
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_55%,#eef5f1_100%)] px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00d09c]/18 bg-[#00d09c]/9 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-[#0b4a2f]">
            <LayoutPanelTop size={14} />
            Filtros
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-bold text-slate-600">
            <SlidersHorizontal size={14} />
            {meta.users.length} usuarios
          </div>

          <ToggleButton
            active={filters.onlyMine}
            icon={UserCheck}
            label="Mis tareas"
            onClick={() => onFiltersChange({ onlyMine: !filters.onlyMine })}
          />

          <ToggleButton
            active={filters.overdueOnly}
            icon={CalendarClock}
            label="Vencidas"
            tone="amber"
            onClick={() =>
              onFiltersChange({ overdueOnly: !filters.overdueOnly })
            }
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <MiniMetric label="Filtradas" value={String(totalTasks)} />
          <MiniMetric label="Vencidas" value={String(overdueCount)} tone="amber" />

          {meta.permissions.can_create && (
            <button
              type="button"
              onClick={onOpenCreate}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_50%,#00a879_100%)] px-4 py-2 text-sm font-black text-white shadow-[0_14px_30px_rgba(11,74,47,.18)] transition hover:-translate-y-0.5"
            >
              <Plus size={15} />
              Nueva tarea
            </button>
          )}
        </div>
      </div>

      <div className="bg-[#fbfdfc] p-4">
        <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1.4fr)_repeat(4,minmax(0,1fr))]">
          <div className="flex items-center gap-2 rounded-xl border border-[#dfe8e4] bg-white px-3.5 py-2.5 shadow-[0_7px_18px_rgba(15,23,42,.04)] focus-within:border-[#00d09c]/45 focus-within:ring-4 focus-within:ring-[#00d09c]/10">
            <Search size={16} className="text-slate-400" />
            <input
              value={filters.search}
              onChange={(event) =>
                onFiltersChange({ search: event.target.value })
              }
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
              placeholder="Buscar por tarea, proyecto o persona..."
            />
          </div>

          <select
            value={filters.estado}
            onChange={(event) =>
              onFiltersChange({
                estado: event.target.value as TaskFilters["estado"],
              })
            }
            className="field-control"
          >
            <option value="">Todos los estados</option>
            {taskStateOrder.map((state) => (
              <option key={state} value={state}>
                {getTaskStateLabel(state)}
              </option>
            ))}
          </select>

          <select
            value={filters.prioridad}
            onChange={(event) =>
              onFiltersChange({
                prioridad: event.target.value as TaskFilters["prioridad"],
              })
            }
            className="field-control"
          >
            <option value="">Todas las prioridades</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>

          <select
            value={filters.proyecto}
            onChange={(event) =>
              onFiltersChange({ proyecto: event.target.value })
            }
            className="field-control"
          >
            <option value="">Todos los proyectos</option>
            {meta.projects.map((project) => (
              <option key={project.id} value={String(project.id)}>
                {project.nombre}
              </option>
            ))}
            <option value="none">Sin proyecto</option>
          </select>

          <select
            value={filters.responsable}
            onChange={(event) =>
              onFiltersChange({ responsable: event.target.value })
            }
            className="field-control"
          >
            <option value="">Todos los responsables</option>
            {meta.users.map((user) => (
              <option key={user.id} value={String(user.id)}>
                {user.nombre}
              </option>
            ))}
            <option value="unassigned">Sin asignar</option>
          </select>
        </div>
      </div>
    </section>
  );
}

function ToggleButton({
  active,
  icon: Icon,
  label,
  onClick,
  tone = "green",
}: {
  active: boolean;
  icon: typeof UserCheck;
  label: string;
  onClick: () => void;
  tone?: "green" | "amber";
}) {
  const activeClass =
    tone === "amber"
      ? "border-amber-300 bg-amber-50 text-amber-800"
      : "border-[#0b4a2f] bg-[#0b4a2f] text-white";

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-black transition",
        active
          ? activeClass
          : "border-[#dfe8e4] bg-white text-slate-600 hover:border-[#00d09c]/24 hover:bg-[#00d09c]/8 hover:text-[#0b4a2f]",
      ].join(" ")}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function MiniMetric({
  label,
  value,
  tone = "green",
}: {
  label: string;
  value: string;
  tone?: "green" | "amber";
}) {
  return (
    <div className="rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-bold text-slate-700">
      <span className="mr-1.5 font-black uppercase tracking-[0.12em] text-slate-400">
        {label}
      </span>
      <span className={tone === "amber" ? "text-amber-700" : "text-slate-950"}>
        {value}
      </span>
    </div>
  );
}
